import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { store } from 'Store';
import { http } from "Utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, Form, Row, Col } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import { TextButton } from "Components/Button";
import { setupMfa, submitMfa } from "./FirstTimeAxiosFuncs";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { ERROR_MESSAGES } from "Utils/constants";
import { PageInfo } from "Components/PageInformation/PageInformation";
import { PATHS } from "Utils/PagePaths";

const FirstTimeSetupMFA = () => {
  const dispatch = useDispatch();
  const loginState = store.getState().loginInfo;
  const navigate = useNavigate();
  const { token } = useParams();
  const location = useLocation();
  const isForward = location.state?.isForward;

  const [isLoading, setIsLoading] = useState(true);
  const [newPhoneRequest, setNewPhoneRequest] = useState({
    mobileNumber: null
  });
  const [mfaToken, setMfaToken] = useState("");
  const [showSendCodeMsg, setShowSendCodeMsg] = useState(false);
  const [disableSendCode, setDisableSendCode] = useState(true);
  const [disableMfaTokenField, setDisableMfaTokenField] = useState(true);
  // error messages display on top 
  const [globalErrorList, setGlobalErrorList] = useState([]);
  const tokenInputRef = useRef();

  // handle phone input field change
  const handlePhoneChange = (value) => {

    //console.log("print out phone change", value, value.length);

    // enable/disable "Send Code"
    if (value.length < 10) {
      setDisableSendCode(true);
    } else {
      setDisableSendCode(false);
    }

    setNewPhoneRequest({
      mobileNumber: value
    });
  }

  // handle six digit mfa token field change
  const handleMfaChange = (e) => {

    let value = e.target.value;
    const regex = /^[0-9\b]+$/;

    //console.log("test mfa code", regex.test(value));

    if (value === '' || regex.test(value)) {
      setMfaToken(e.target.value);
    }
  }

  // api to send code with new phone number
  const handleSendCode = async () => {
    // clear error
    setGlobalErrorList([]);
    // disable the send code button to avoid double click renable it after 30s
    setDisableSendCode(true);

    //const res = await setupUserSmsFactor(token, newPhoneRequest);
    const res = await setupMfa(token, newPhoneRequest)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });
    //console.log("handleSendCode()", res);

    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
      setDisableSendCode(false);
    } else {
      setTimeout(() => {
        setDisableSendCode(false);
      }, 30000);
      setShowSendCodeMsg(true);
      setDisableMfaTokenField(false);
      setTimeout(() => {
        tokenInputRef.current.focus();
      }, 500);
    }
  }

  // api to verify the new phone device
  const verifyToken = async () => {
    // clear error
    setGlobalErrorList([]);
    setShowSendCodeMsg(false);

    const res = await submitMfa(token, mfaToken)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });
    //console.log("verifyToken()", res);
    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
    } else {
      // update success and back to login page
      navigate(PATHS.public['Sign In'], { state: { message: "Your account has been successfully activated. Please sign in." } });
    }
  }

  /*
  UserActivationController.java 
  url: /api/activation/validateToken
  ValidateResetPasswordTokenRequest : {
   String resetPasswordToken
  }
   */
  const validateToken = async (token) => {

    // the activation can only be operated when the user is not login!
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to activate the account.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });
      //stay on the term of use page
      return;
    }

    var url = "/api/activation/validateToken";
    var body = {
      resetPasswordToken: token
    }

    return http.post(url, body, {
      headers: {
        //"Authorization": token
      }
    }).then((res) => {
      //console.log("validateToken", res);
      if (res.status !== 1) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: res.globalErrorMessageList,
            showContactHelp: false
          },
          redirect: {
            redirect: true,
            redirectFirst: true,
            path: PATHS.public['Sign In'],
            data: {}
          }
        });
        return;
      }

      if (res.status === 1) {
        if (isForward) {
          setIsLoading(false);
          window.history.replaceState({}, '');
          return;
        }

        // back to agree terms of use
        navigate("/activation/token/" + token);
        return;
      }
    }).catch((error) => {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });
  }

  useEffect(() => {
    validateToken(token);
  }, [])

  // back to login
  const cancel = () => {
    setMfaToken("");
    navigate(PATHS.public['Sign In']);
  }

  return (
    <>
      {isLoading ? null :
        <Row className="mb-3">
          <Col xl={10} lg={11} md={11} sm={12}>
            <Card id="firsttimepwd-sms-mfa-setup" className="p-4">
              <Card.Body className="text-start">
                <h1 id="mainContent" className="text-start mb-3 blue fw-bold">
                  Two-Factor Authentication
                </h1>
                <hr></hr>
                {showSendCodeMsg ?
                  <>
                    <PageInfo className="mb-3" type="info" message={"A code has been sent to your registered cell phone. Please enter that code in the field below. You may send a new code after 30 seconds if needed."}></PageInfo>
                    <br />
                  </>
                  : null}
                {globalErrorList.map((item, index) =>
                  <>
                    <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                    <br />
                  </>
                )}
                <p className="text-start pb-2">
                  To set up you authentication phone number, follow the steps below.
                </p>
                <Form>
                  <Form.Group as={Row} className="mb-3" controlId="mfaPhone">
                    <Form.Label column sm="5" className="mfa-setting-label">
                      <div class="mfa-setting-step">
                        <span>1.</span>
                        <span>Enter a ten digit phone number</span>
                      </div>
                    </Form.Label>
                    <Col sm={3} xs={9}>
                      <PhoneInput
                        defaultMask="(...) ...-...."
                        alwaysDefaultMask="true"
                        country={"us"}
                        autoFormat="true"
                        enableAreaCodes="true"
                        disableCountryCode="true"
                        disableDropdown="true"
                        disableSearchIcon="true"
                        disableCountryGuess="true"
                        buttonStyle={{ display: 'none' }}
                        inputClass={"form-control"}
                        placeholder={""}
                        inputProps={{
                          id: "mfaPhone"
                        }}
                        onChange={handlePhoneChange}
                      />
                    </Col>
                  </Form.Group>
                  <Row className="mb-4 align-items-center">
                    <Col sm="5" className="pt-2 mfa-setting-label">
                      <div class="mfa-setting-step">
                        <span>2.</span>
                        <span>Click to send code to your registered mobile device</span>
                      </div>
                    </Col>
                    <Col>
                      <TextButton
                        className="mt-1"
                        disabled={disableSendCode}
                        variant="normal"
                        onClick={handleSendCode}
                        content={"Send Code"}
                      >
                      </TextButton>
                    </Col>
                  </Row >
                  <Form.Group as={Row} className="mb-3" controlId="mfaToken">
                    <Form.Label column sm="5" className="mfa-setting-label">
                      <div class="mfa-setting-step">
                        <span>3.</span>
                        <span>Enter the six digit code</span>
                      </div>
                    </Form.Label>
                    <Col sm={3} xs={9}>
                      <Form.Control
                        ref={tokenInputRef}
                        disabled={disableMfaTokenField}
                        type="text"
                        maxlength={6}
                        value={mfaToken}
                        onChange={handleMfaChange}
                      />
                    </Col>
                  </Form.Group>
                  <Row className="mb-3 pt-1 align-items-center">
                    <Col sm="5" className="pt-2 mfa-setting-label">
                      <div class="mfa-setting-step">
                        <span>4.</span>
                        <span>Click Verify to confirm</span>
                      </div>
                    </Col>
                    <Col className="mt-1">
                      <TextButton
                        className="w-10"
                        variant="normal"
                        disabled={mfaToken.length !== 6}
                        onClick={verifyToken}
                        content={"Verify"}
                      >
                      </TextButton>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      <TextButton
                        className="w-10"
                        variant="outline"
                        onClick={cancel}
                        content={"Cancel"}
                      >
                      </TextButton>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>}
    </>
  )
}

export default FirstTimeSetupMFA;